import { Listbox } from "@headlessui/react"
import classNames from "classnames"
import React from "react"
import styled from "styled-components"
import { PrimitiveBaseProps, PrimitiveWithSizing } from "../helpers/types"
import { useSizing } from "../helpers/use-sizing"
import { Icon } from "./icon"

interface Props<T>
  extends Omit<React.ButtonHTMLAttributes<HTMLDivElement>, "value" | "onChange">,
    PrimitiveBaseProps<HTMLDivElement>,
    PrimitiveWithSizing {
  value?: T
  onChange: (value: T) => void
  trigger: React.ReactNode
  placeholder?: string
}

// taken from https://headlessui.dev/react/listbox
const Root = styled.div`
  position: relative;
  min-width: 18rem; // 288px, same as w-72
`

const StyledTrigger = styled(Listbox.Button)`
  width: 100%;
  position: relative;
  padding: 0px 40px 0px 12px;
  background: white;
  border-radius: 4px;
  border: 1px solid rgb(209, 213, 219); // gray30 (tailwind)
  cursor: pointer;
  display: flex;
  height: 42px;
  align-items: center;
`

const StyledOptions = styled(Listbox.Options)`
  position: absolute;
  width: 100%;
  margin-top: 4px;

  overflow: auto;
  background: white;
  border-radius: 4px;
  max-height: 240px;
  padding-top: 4px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
`

const StyledTriggerIcon = styled(Icon)`
  position: absolute;
  right: 12px;
`

const StyledOption = styled.li<{ active: boolean; selected: boolean }>`
  background: ${(props) => (props.active || props.selected ? "#efefef" : "white")};
  cursor: pointer;
  padding: 8px 16px 8px 16px;
`

export function Select<T>(props: Props<T>) {
  const {
    children,
    className,
    value,
    onChange,
    trigger,
    placeholder,
    innerRef,
    mainAxisSizing = "hug",
    crossAxisSizing = "hug",
    ...otherProps
  } = props

  // HACK: ensure the select list doesn't collapse down on itself if there isn't a placeholder
  const nbspPlaceholder = placeholder === "" ? "\xa0" : placeholder
  const sizingClassName = useSizing(mainAxisSizing, crossAxisSizing)

  return (
    <Listbox value={value} onChange={onChange} {...otherProps}>
      <Root ref={innerRef} className={classNames(className, sizingClassName)}>
        {value || nbspPlaceholder === undefined ? trigger : <SelectTrigger>{nbspPlaceholder}</SelectTrigger>}
        {children && <StyledOptions>{children}</StyledOptions>}
      </Root>
    </Listbox>
  )
}

const SelectTrigger = (props: React.ComponentProps<"button">) => {
  const { children, ...otherProps } = props
  return (
    <StyledTrigger {...otherProps}>
      {children}
      <StyledTriggerIcon name="FeatherChevronDown" />
    </StyledTrigger>
  )
}
SelectTrigger.displayName = "SelectTrigger"

interface OptionProps<T> extends Omit<React.ComponentProps<"li">, "value"> {
  value: T
}
function SelectOption<T>(props: OptionProps<T>) {
  const { children, className, ...otherProps } = props
  return (
    <Listbox.Option {...otherProps}>
      {({ active, selected }) => (
        <StyledOption className={className} active={active} selected={selected}>
          {children}
        </StyledOption>
      )}
    </Listbox.Option>
  )
}

Select.Trigger = SelectTrigger
Select.Option = SelectOption
